
import { CloseFullscreen, OpenInFull, Tune } from "@mui/icons-material";
import { Box, IconButton, Menu, Divider, MenuItem, ListItemIcon, Typography, Tooltip, createTheme, Stack, ListSubheader } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { AppSettingsActionTypeEnum, useAppSettings } from "../../contexts/app-context/AppContext";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "./ViewOptionStateEnum";
import MoreIcon from '@mui/icons-material/MoreVert';
import { isMobile, isTablet } from 'react-device-detect';
import { keyframes } from '@mui/material/styles';
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import _ from "lodash";
import { ConfigurationIcon } from "../action-menu/ActionMenuIcons";
import { useTranslation } from "react-i18next";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import Enumerable from "linq";
import { ActionMenuItemCategoryEnum } from "../action-menu/BaseActions";
import { subscribe, unsubscribe } from "pubsub-js";
import { EXECUTE_ACTION_TOPIC, ExecuteActionMsg } from "../../common/models/pub-sub/PubSubTopics";
import { useAppHeaderContext } from "../../contexts/app-header-context/UseAppHeaderContext";

const glow = keyframes` 
    from {
        box-shadow: none;
        background-color: transparent;
    }
    to {
        box-shadow: 0 0 20px 8px rgba(41, 57, 85, .4);
        background-color: rgba(41, 57, 85, .4);
    }`;

export type ContextActionMenuV2Props = {
    viewOptionState?: ViewOptionStateEnum;
    onExecuteAction?: (action: ActionMenuItem) => void;
    actions?: ActionMenuItem[];
    onOverrideViewOptionsClick?: () => void;
    hideOptionsAction?: boolean;
    hideAddToFavoritesAction?: boolean;
    buttonColor?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    showMaximizeViewAction?: boolean;
    maximizeView?: boolean;
    configurationActions?: ActionMenuItem[];
    dense?: boolean;
};

export function ContextActionMenuV2({
    actions,
    viewOptionState,
    onExecuteAction,
    onOverrideViewOptionsClick,
    hideOptionsAction,

    buttonColor,
    showMaximizeViewAction,
    maximizeView,
    configurationActions,
    dense
}: ContextActionMenuV2Props) {

    const [primaryActions, setPrimaryActions] = useState<ActionMenuItem[]>([]);
    const [overflowActions, setOverflowActions] = useState<ActionMenuItem[]>([]);
    const {t} = useTranslation();
    const [subActionAnchorElements, setSubActionAnchorElements] = useState<HTMLButtonElement[]>([]);
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement>();
    const [configurationAnchorElement, setConfigurationAnchorElement] = useState<HTMLButtonElement>();
    const isPhone = isMobile && !isTablet;
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const [offlineState] = useOfflineDetectContext();

    const {
        //appHeaderContextState, 
        setRefreshActionState,
        setExportActionState,
        setFavoriteActionState
    } = useAppHeaderContext();


    const computedConfigurationActions = useMemo(() => {

        const items = configurationActions ? configurationActions : actions?.find(o => o.action === BaseActionEnum.Configure)?.subActions;
        return items ?? [];

    }, [
        configurationActions,
        actions
    ]);

    const activeConfigurationActions = computedConfigurationActions.some(o => !o.disabled);

    const mainTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    // const theme = useMemo(() => {
    //     return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //         createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    // }, [
    //     appSettings.themeSet,
    //     isPhone]
    // );

    const refreshAction = useMemo(() => {
        return actions?.find(o => o.action === BaseActionEnum.Refresh);
    }, [
        actions
    ]);

    const exportAction = useMemo(() => {
        return actions?.find(o => o.action === BaseActionEnum.Export);
    }, [
        actions
    ]);

    const favoriteAction = useMemo(() => {
        return actions?.find(o => o.action === BaseActionEnum.Favorite);
    }, [
        actions
    ]);

    const open = Boolean(anchorElement);
    const configurationOpen = Boolean(configurationAnchorElement);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setAnchorElement(event.currentTarget);
    };

    const handleConfigurationClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setConfigurationAnchorElement(event.currentTarget);
    };

    const handleClose = (item: ActionMenuItem) => {
        setAnchorElement(undefined);
        setConfigurationAnchorElement(undefined);
        onExecuteAction?.(item);
    };


    useEffect(() => {

        if (actions) {

            // the "NEW UI/UX" improvements require these actions to be in the main header
            // however, it's all behind a feature flag so this is a shortcut to prevent
            // modifying in all of the views
            const newActions = _.cloneDeep(actions.filter(o => o.action !== BaseActionEnum.Configure &&
                o.action !== BaseActionEnum.Excel &&
                o.action !== BaseActionEnum.PDF &&
                o.action !== BaseActionEnum.Export &&
                o.action !== BaseActionEnum.Refresh &&
                o.action !== BaseActionEnum.Favorite
            ));
            const overflowCount =   2;
            const primaryActions = newActions.filter(o => o.id !== 'moreActions').slice(0, overflowCount);

            let overflow: ActionMenuItem[] = [];

            //newActions.slice(overflowCount).forEach((o) => {
            //if (newActions.length > 1 || (newActions.at(0)?.subActions?.length ?? 0 )> 1) {
                newActions.forEach((o) => {

                    if (o.subActions) {
                        overflow = overflow.concat(o.subActions);
                    } else {
                        overflow.push(o);
                    }
                });
            //}

            // overflow.filter(o => o.action === BaseActionEnum.Excel || o.action === BaseActionEnum.PDF)
            // .forEach(o => o.label = `Export ${o.label}`);

            // if (overflow.length === 1) {
            //     primaryActions.push(overflow[0]);
            //     overflow = [];
            // }

            setPrimaryActions(primaryActions);
            setOverflowActions(overflow);
        }

    }, [
        actions,
        setPrimaryActions,
        setOverflowActions,
        viewOptionState,
        hideOptionsAction
    ]);



    const groupedActions: IKeyValuePair<string, ActionMenuItem[]>[] = Enumerable.from(overflowActions)
        .groupBy(o => o.category ?? ActionMenuItemCategoryEnum.NONE)
        .select(o => {

            const key = o.key().toString();

            return {
                //Key: o.key() !== undefined ? ActionMenuItemCategoryEnum[key] : '',
                Key: key,
                Value: o.toArray().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

            } as IKeyValuePair<string, ActionMenuItem[]>
        })
        .toArray();


    useEffect(() => {
        setRefreshActionState(refreshAction !== undefined && refreshAction.disabled !== true);

        const excelAction = exportAction?.subActions?.find(o => o.action === BaseActionEnum.Excel);
        const pdfAction = exportAction?.subActions?.find(o => o.action === BaseActionEnum.PDF);
        const saveGraphImageAction = exportAction?.subActions?.find(o => o.action === BaseActionEnum.SaveGraphImage);
        const saveQuickGraphAction = exportAction?.subActions?.find(o => o.action === BaseActionEnum.SaveQuickGraph);

        setExportActionState({
            exportExcelEnabled: exportAction !== undefined && excelAction !== undefined && exportAction.disabled !== true && excelAction.disabled !== true,
            exportPDFEnabled: exportAction !== undefined && pdfAction !== undefined && exportAction.disabled !== true && pdfAction.disabled !== true,
            saveGraphImageEnabled: saveGraphImageAction !== undefined && saveGraphImageAction.disabled !== true,
            saveQuickGraphEnabled: saveQuickGraphAction !== undefined && saveQuickGraphAction.disabled !== true
        });

        setFavoriteActionState(favoriteAction !== undefined && favoriteAction.disabled !== true);

    }, [
        refreshAction,
        exportAction,
        favoriteAction,
        setRefreshActionState,
        setExportActionState,
        setFavoriteActionState
    ]);

    useEffect(() => {

        if ((refreshAction || exportAction) && onExecuteAction) {
            const token = subscribe(EXECUTE_ACTION_TOPIC, (message, data) => {
                const actionData = data as ExecuteActionMsg;
                if (actionData.action === BaseActionEnum.Refresh && refreshAction) {
                    onExecuteAction(refreshAction);
                } else if ((actionData.action === BaseActionEnum.Excel || 
                            actionData.action === BaseActionEnum.PDF ||
                            actionData.action === BaseActionEnum.SaveGraphImage ||
                            actionData.action === BaseActionEnum.SaveQuickGraph) && exportAction) {
                    const subAction = exportAction.subActions?.find(o => o.action === actionData.action);
                    if (subAction) {
                        onExecuteAction(subAction);
                    }
                }
            });
    
            return () => {
                if (token) {
                    unsubscribe(token);
                }
            }
        }

    }, [
        refreshAction,
        exportAction,
        onExecuteAction
    ]);


    return (
        <Stack
            direction='row'
            sx={{
                alignItems: 'flex-end',
                marginLeft: 'auto'
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flex: '1 1 auto',
                    justifyContent: 'flex-end',
                    marginBottom: 'auto',
                    marginRight: 'auto',
                    alignItems: 'center',
                    paddingTop: isPhone || dense ? 1.5 : 3,
                    "& button": {
                        height: '28px'  // must force the button height to squish it down
                    },
            }}>

                {!dense && <Tooltip title='View Options'>
                    <IconButton size="large"
                        disabled={hideOptionsAction || viewOptionState === ViewOptionStateEnum.Hidden}
                        aria-label="options"
                        // color={viewOptionState === ViewOptionStateEnum.Changed ? 'secondary' : buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                        color={viewOptionState === ViewOptionStateEnum.Changed ? 'secondary' : buttonColor ? buttonColor : "inherit"}
                        edge='end'
                        sx={{ 
                            marginLeft: 'auto',
                            // User Story 54279: Research: 2026 Q4 - Review GAS Report 
                            marginBottom: undefined
                        }}
                        onClick={() => {
                            if (onOverrideViewOptionsClick) {
                                onOverrideViewOptionsClick();
                            } else {
                                dispatchAppSettings({
                                    type: AppSettingsActionTypeEnum.SideSheetOpenChanged,
                                    open: !appSettings.sideSheetOpen
                                });
                            }
                        }}>
                        <Tune />
                    </IconButton>
                </Tooltip> 
                }

                {
                //!offlineState.offline && (computedConfigurationActions?.length ?? 0) > 0  ? 
                !offlineState.offline && !dense && activeConfigurationActions ? 
                <>
                    {
                    computedConfigurationActions.length > 0 && <Tooltip title='Configuration Options'>
                        <IconButton
                            size="large"
                            edge="end"
                            //disabled={computedConfigurationActions.length === 0}
                            //color={isPhone ? "inherit" : 'default'}
                            color= "inherit"
                            sx={{
                                color: isPhone && computedConfigurationActions.length === 0 ? 'white!important' : undefined,
                                opacity: isPhone && computedConfigurationActions.length === 0 ? .3 : undefined
                            }}
                            onClick={handleConfigurationClick}
                            
                        >
                            <ConfigurationIcon />
                        </IconButton>
                    </Tooltip>
                    }
                    <ThemeProvider theme={mainTheme}>
                    <Menu
                        id={"configuration-menu"}
                        color='textPrimary'
                        anchorEl={configurationAnchorElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}
                        keepMounted
                        open={configurationOpen}
                        onClose={handleClose}>
                        {computedConfigurationActions?.map((item, index) => [

                            item.dividerBefore && <Divider key={`db${index}`}/>,
                            <MenuItem
                                key={`mi${index}`}
                                disabled={item.disabled}
                                onClick={() => handleClose(item)}
                                sx={{
                                    textDecoration: item.disabled ? 'line-through' : undefined
                                }}
                                >
                                <ListItemIcon sx={{ minWidth: theme => theme.spacing(4) }}>
                                    {
                                        item.icon ? item.checked && item.checkedIcon ?
                                            <item.checkedIcon /> :
                                            <item.icon /> :
                                            undefined
                                    }
                                </ListItemIcon>
                                <Typography variant='inherit'>
                                    {item.label}
                                </Typography>
                            </MenuItem>,

                            item.dividerAfter &&
                            //overflowActions.length > 1 &&
                            <Divider key={`da${index}`}/>
                        ])}
                    </Menu>
                    </ThemeProvider>
                </>
                : null
                }
            </Box>
            {/* {primaryActions.length > 0 &&  */}
            <>
                {!dense && <Divider 
                    orientation="vertical" 
                    flexItem={true} 
                    sx={{
                        display: isPhone && primaryActions.length === 0 ? 'none' : undefined,
                        marginLeft: 1,
                        marginTop: '6px',
                        borderColor: buttonColor ? buttonColor :"inherit",
                        borderRightWidth: isPhone ? 'medium' : undefined
                    }} 
                />}
                
                <Stack 
                    direction='column'
                    sx={{
                    overflow: 'hidden',
                    }}
                >

                {!isPhone && !dense &&
                <Typography
                    variant="caption"
                    sx={{
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        //color: theme => buttonColor ? buttonColor : isPhone ? "inherit" : theme.palette.grey[600],
                        color: theme => buttonColor ? buttonColor : "inherit",
                        opacity: 0.7,
                        paddingLeft: 2
                    }}
                >
                    {t('pageActions')}
                </Typography>
                }

                <Box
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto',
                        justifyContent: 'flex-end',
                        marginBottom: 'auto',
                        marginRight: 'auto',
                        alignItems: 'center',
                        "& button": {
                            height: '28px'
                        }
                    }}
                >
                    {
                    showMaximizeViewAction ? 
                    <Tooltip title={maximizeView ? "Restore" : "Maximize"}>
                        <IconButton size="large"
                            aria-label="options"
                            edge='end'
                            //sx={{ marginLeft: 'auto' }}
                            sx={{
                                marginBottom: dense ? '4px' : undefined,
                                color: "inherit"
                            }}
                            onClick={
                                () => onExecuteAction && onExecuteAction({ 
                                    label: 'Maximize/Restore',
                                    action: BaseActionEnum.ExpandCollapse 
                                })
                            }>
                            {maximizeView ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    : 
                    null
                    }

                    {primaryActions.length === 0 && 
                    <IconButton
                            size="large"
                            edge="end"
                            sx={{
                                visibility: 'hidden'
                            }}
                        >
                        </IconButton>
                    }

                    {
                    primaryActions.map((o, index) =>
                        <React.Fragment key={index}>
                            <Tooltip title={o.label} key={index}>
                                <Box component='span'>
                                    <IconButton
                                        id={o.id}
                                        size="large"
                                        disabled={o.disabled}
                                        aria-label={o.label}
                                        edge="end"
                                        //color={buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                                        color={buttonColor ? buttonColor : "inherit"}                                        
                                        onClick={(event) => {
                                                if (o.subActions) {
                                                    setSubActionAnchorElements([...subActionAnchorElements, event.currentTarget]);
                                                } else {
                                                    onExecuteAction?.(o);
                                                }
                                        }}
                                        sx={{ 
                                            animation: o.pulse ? 
                                                `${glow} 1.5s ease-in-out infinite alternate` : 
                                                undefined ,
                                            // color: isPhone && o.disabled ? 'white!important' : undefined,
                                            color: isPhone && o.disabled ? 'white!important' : "inherit",
                                            opacity: isPhone && o.disabled ? .3 : undefined
                                        }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            flex: "0 0 auto",
                                            border: theme => o.selected
                                                ? `solid 1px ${theme.palette.text.primary}`
                                                : "none",
                                            borderRadius: "4px"
                                        }}>
                                            {<o.icon />}
                                        </Box>
                                    </IconButton>
                                </Box>
                            </Tooltip>
                            {o.subActions &&

                            <ThemeProvider theme={mainTheme}>
                                <Menu
                                    id={`subaction-menu`}
                                    color='textPrimary'
                                    anchorEl={subActionAnchorElements.find(v => v.id === o.id)}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center'
                                    }}
                                    keepMounted
                                    open={Boolean(subActionAnchorElements.find(v => v.id === o.id))}
                                    onClose={() => setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== o.id))}
                                >
                                    {o.subActions.map((item, index) => [
                                        item.dividerBefore && <Divider key={`db${index}`}/>,
                                        <MenuItem
                                            key={`mi${index}`}
                                            disabled={item.disabled}
                                            onClick={() => {
                                                setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== o.id));
                                                onExecuteAction?.(item);
                                            }}>
                                            {item.icon && 
                                            <ListItemIcon id={`${item.id}`} sx={{ minWidth: theme => theme.spacing(4)}}>
                                                {item.checked && item.checkedIcon ? 
                                                    <item.checkedIcon /> : 
                                                        <item.icon />
                                                }
                                            </ListItemIcon>
                                            }
                                            <Typography variant='inherit'>
                                                {item.label}
                                            </Typography>
                                        </MenuItem>,

                                        item.dividerAfter &&
                                        <Divider key={`da${index}`}/>
                                    ])}
                                </Menu>
                            </ThemeProvider>                                                            
                            }
                        </React.Fragment>
                    )}
                    {
                    overflowActions.length > 0 &&
                    <>
                    <Tooltip title='More Options'>
                        <IconButton
                            size="large"
                            aria-label="display more actions"
                            edge="end"
                            //color={buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                            color={buttonColor ? buttonColor : "inherit"}
                            onClick={handleClick}>
                            <MoreIcon />
                        </IconButton>
                    </Tooltip>
                    <ThemeProvider theme={mainTheme}>
                    <Menu
                        id={"more-options-menu"}
                        color='textPrimary'
                        anchorEl={anchorElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}
                        keepMounted
                        open={open}
                        onClose={handleClose}>
                        {

                            groupedActions.map((o, ondx) => [
                            <ListSubheader key={ondx} 
                                sx={{
                                    fontSize: '.90rem',
                                    lineHeight: '30px'
                                }}>
                                {o.Key}
                            </ListSubheader>

                            ,o.Value.map((item, index) => (
                                [
                                <MenuItem
                                    key={`mi${index}`}
                                    disabled={item.disabled}
                                    sx={{
                                        textDecoration: item.disabled ? 'line-through' : undefined
                                    }}
                                    onClick={() => handleClose(item)}
                                    >
                                    <ListItemIcon sx={{ minWidth: theme => theme.spacing(4) }}>
                                        {
                                            item.icon ? item.checked && item.checkedIcon ? 
                                            <item.checkedIcon /> : 
                                            <item.icon /> :
                                            undefined
                                        }
                                    </ListItemIcon>
                                    <Typography variant='inherit'>
                                        {item.label}
                                    </Typography>
                                </MenuItem>,
                                item.dividerAfter && <Divider />
                                ]
                            ))
                            
                            

                            ,

                            ondx < groupedActions.length - 1 ? <Divider key={`dV${ondx}`}/> : null

                        ])



                        }

                        </Menu>
</ThemeProvider>                        
                    </>
                    }
                </Box>
                </Stack>
            </>
            {/* } */}
        </Stack>
    );
}
